import { useEffect, useState, useRef } from 'react';
import type { TouchEvent } from 'react';
import { Link } from 'react-router-dom';
import type { Movie } from '@/mocks/movies';

interface HeroCarouselProps {
  items: Movie[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [current, setCurrent] = useState(0);
  const timerRef = useRef<number | null>(null);
  const touchStartX = useRef<number>(0);

  useEffect(() => {
    timerRef.current = window.setInterval(() => {
      setCurrent((prev) => (prev + 1) % items.length);
    }, 5500);
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
    };
  }, [items.length]);

  const go = (index: number) => {
    if (timerRef.current) window.clearInterval(timerRef.current);
    setCurrent((index + items.length) % items.length);
  };

  const handleTouchStart = (e: TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: TouchEvent) => {
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) {
      go(delta > 0 ? current - 1 : current + 1);
    }
  };

  return (
    <section
      className="relative w-full h-[440px] md:h-[600px] overflow-hidden"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {items.map((movie, index) => (
        <div
          key={movie.id}
          className={`absolute inset-0 transition-opacity duration-700 ${index === current ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        >
          <img
            src={movie.cover}
            alt={`${movie.title}焦点图`}
            title={`${movie.title} - 汉字解析与剧情介绍`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-secondary-950/90 via-secondary-950/60 to-secondary-950/20"></div>
          <div className="absolute inset-0 flex items-center">
            <div className="w-full px-4 md:px-8">
              <div className="max-w-2xl animate-fade-up">
                <div className="flex items-center gap-2 mb-3 md:mb-4">
                  <span className="px-2.5 py-1 rounded-full bg-accent-500 text-secondary-950 text-xs font-label font-semibold">
                    精选焦点
                  </span>
                  <span className="px-2.5 py-1 rounded-full bg-background-50/15 text-background-50 text-xs font-label backdrop-blur">
                    {movie.type} · {movie.year} · {movie.region}
                  </span>
                </div>
                <h2 className="font-heading font-bold text-background-50 text-3xl md:text-5xl leading-tight">
                  {movie.title}
                </h2>
                <p className="mt-3 md:mt-5 max-w-xl text-sm md:text-base font-label text-background-100/90 line-clamp-3">
                  {movie.synopsis}
                </p>
                <div className="mt-4 md:mt-5 flex items-center gap-2 text-background-100">
                  <i className="ri-star-fill text-accent-400 w-4 h-4 flex items-center justify-center"></i>
                  <span className="font-label font-semibold">{movie.rating.toFixed(1)}</span>
                  <span className="text-background-300/80 text-sm font-label">· {movie.hanziTitle}</span>
                </div>
                <div className="mt-6 md:mt-8 flex flex-wrap items-center gap-3">
                  <Link
                    to={`/detail/${movie.id}`}
                    className="px-6 py-2.5 rounded-md bg-accent-500 hover:bg-accent-600 text-secondary-950 text-sm font-label font-semibold whitespace-nowrap transition-colors cursor-pointer"
                  >
                    查看汉字解析
                  </Link>
                  <Link
                    to="/category"
                    className="px-6 py-2.5 rounded-md bg-background-50/10 hover:bg-background-50/20 text-background-50 text-sm font-label border border-background-50/30 whitespace-nowrap transition-colors cursor-pointer"
                  >
                    浏览全部影视
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}

      <button
        type="button"
        onClick={() => go(current - 1)}
        className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 items-center justify-center rounded-full bg-background-50/15 hover:bg-background-50/30 text-background-50 backdrop-blur cursor-pointer"
        aria-label="上一张"
      >
        <i className="ri-arrow-left-s-line text-2xl"></i>
      </button>
      <button
        type="button"
        onClick={() => go(current + 1)}
        className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 items-center justify-center rounded-full bg-background-50/15 hover:bg-background-50/30 text-background-50 backdrop-blur cursor-pointer"
        aria-label="下一张"
      >
        <i className="ri-arrow-right-s-line text-2xl"></i>
      </button>

      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-2">
        {items.map((movie, index) => (
          <button
            key={movie.id}
            type="button"
            onClick={() => go(index)}
            aria-label={`切换到第 ${index + 1} 张`}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${index === current ? 'w-7 bg-accent-500' : 'w-3 bg-background-50/50'}`}
          ></button>
        ))}
      </div>
    </section>
  );
}